import { useState, useEffect, useCallback } from 'react';
import { fetchData } from '../api';

export interface DebugLogEntry {
  timestamp: number;
  type: 'read' | 'write' | 'control' | 'error' | 'info';
  portId?: string;
  message: string;
  responseTime?: number;
  success: boolean;
}

export interface PortStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  avgResponseTime: number;
  lastResponseTime: number;
  packetLossPercent: number;
}

export interface PortDebugData {
  deviceType: string;
  stats: PortStats;
  log: DebugLogEntry[];
}

export interface DebugLogsData {
  ports: { [portId: string]: PortDebugData };
  aggregate: {
    stats: PortStats;
    log: DebugLogEntry[];
  };
}

export function useDebugLogs(enabled: boolean = true, pollInterval: number = 2000) {
  const [data, setData] = useState<DebugLogsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await fetchData('/debugLogs');
      if (result) {
        setData(result as DebugLogsData);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch debug logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    refresh();
    const interval = setInterval(refresh, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval, refresh]);

  return { data, loading, error, refresh };
}
